import { GROUPS } from "../sections/groups";
import { ROW_HELP, SECTION_HELP } from "../sections/help";
import { CUSTOM_TERMS } from "../sections/searchIndex.gen";
import type { RowDef, SectionDef, TabId } from "./schema";

/**
 * Find anything in Advanced.
 *
 * The old filter was one substring over a section's iJewel title and its row
 * labels — so "glow" found nothing (the section is "Bloom"), a plain title we
 * show on screen could not be searched, and the ~80 custom rows (their labels
 * live inside the component) were invisible. Now:
 *
 *   - every word of the query must match (order free): "shadow soft" finds
 *     the soft-shadow rows, not everything with either word;
 *   - each word may match through a synonym — what a jeweller or a Blender /
 *     KeyShot user would type for the same thing ("glow" → bloom, "fire" →
 *     dispersion, "hdri" → environment);
 *   - it reads the shown title AND iJewel's title, the section and row help,
 *     the group name, row keywords, dropdown options, button labels and the
 *     generated index of what each custom row renders (searchIndex.gen.ts).
 *
 * A section whose own text matches keeps all its rows; otherwise only the
 * rows that match are kept.
 */

/** word → other words that mean the same thing here. Keys are lower case. */
export const SYNONYMS: Record<string, string[]> = {
  glow: ["bloom"],
  halo: ["bloom"],
  sparkle: ["dispersion", "brilliance", "bloom"],
  fire: ["dispersion", "rainbow"],
  rainbow: ["dispersion"],
  brilliance: ["bounces", "gamma"],
  shine: ["reflection", "roughness", "specular"],
  shiny: ["reflection", "roughness"],
  gloss: ["roughness", "clearcoat"],
  polish: ["roughness"],
  matte: ["roughness"],
  brushed: ["anisotropy", "roughness"],
  mirror: ["reflection", "ssr"],
  reflect: ["reflection", "ssr"],
  ssr: ["screen space reflections", "reflections"],
  ssao: ["ambient occlusion"],
  ao: ["ambient occlusion"],
  gi: ["global illumination", "ssgi"],
  aa: ["anti-aliasing", "taa", "antialias"],
  antialiasing: ["anti-aliasing", "taa"],
  jaggies: ["anti-aliasing", "taa"],
  smooth: ["anti-aliasing", "taa", "damping"],
  hdr: ["environment", "hdri"],
  hdri: ["environment", "hdr"],
  env: ["environment"],
  lighting: ["light", "environment"],
  lights: ["light"],
  bright: ["exposure", "intensity", "brightness"],
  brightness: ["exposure", "intensity"],
  dark: ["exposure", "intensity"],
  contrast: ["tonemapping", "tone mapping", "gamma"],
  tone: ["tonemapping", "tone mapping"],
  blur: ["depth of field", "dof", "bokeh"],
  bokeh: ["depth of field", "dof"],
  focus: ["depth of field", "dof"],
  dof: ["depth of field"],
  floor: ["ground"],
  plane: ["ground"],
  table: ["ground"],
  shadow: ["shadows", "occlusion"],
  backdrop: ["background"],
  bg: ["background"],
  colour: ["color"],
  color: ["colour"],
  grey: ["gray"],
  gray: ["grey"],
  resolution: ["render scale", "pixel ratio", "size"],
  sharp: ["sharpen", "anti-aliasing"],
  noise: ["grain"],
  gem: ["diamond", "stone"],
  stone: ["gem", "diamond"],
  diamond: ["gem", "stone"],
  metal: ["metalness"],
  gold: ["metal"],
  spin: ["rotate", "turntable", "auto rotate"],
  turntable: ["rotate", "auto rotate"],
  zoom: ["dolly", "distance"],
  fov: ["field of view"],
  export: ["download", "snapshot"],
  screenshot: ["snapshot", "image export"],
  photo: ["snapshot", "image export"],
  video: ["mp4", "webm", "turntable"],
  glb: ["gltf", "3d file"],
  outline: ["selection"],
  watermark: ["branding", "logo"],
  logo: ["branding", "watermark"],
  lut: ["colour lookup", "grade"],
  grade: ["lut", "tonemapping"],
  vignette: ["vignette"],
  dust: ["grain"],
};

/** Lower-cased query words, de-duplicated, empty words dropped. */
export function tokenize(query: string): string[] {
  return [...new Set(query.toLowerCase().split(/[\s,]+/).map((w) => w.trim()).filter(Boolean))];
}

/** Every spelling a word may match: itself, its synonyms, and a plural/singular. */
export function expand(word: string): string[] {
  const out = new Set<string>([word, ...(SYNONYMS[word] ?? [])]);
  if (word.length > 3 && word.endsWith("s")) out.add(word.slice(0, -1));
  return [...out];
}

/** Words to mark on screen: what was typed plus the synonyms that can match. */
export function markTerms(query: string): string[] {
  return [...new Set(tokenize(query).flatMap(expand))];
}

const GROUP_LABEL = new Map(GROUPS.map((g) => [g.id as string, g.label]));

function sectionText(s: SectionDef, tabId: TabId): string {
  return [s.title, s.displayTitle, SECTION_HELP[s.id], s.info, GROUP_LABEL.get(tabId)].filter(Boolean).join(" \n ").toLowerCase();
}

function rowText(s: SectionDef, r: RowDef): string {
  const parts: (string | undefined)[] = [r.label, r.hint, r.keywords, ROW_HELP[`${s.id}.${r.id}`]?.text];
  if (r.kind === "button") parts.push(...r.buttons.map((b) => b.label));
  if (r.kind === "dropdown" && Array.isArray(r.options)) parts.push(...r.options.map((o) => o.label));
  if (r.kind === "textAction") parts.push(r.action, r.placeholder);
  if (r.kind === "note" && typeof r.text === "string") parts.push(r.text);
  if (r.kind === "custom") parts.push(...(CUSTOM_TERMS[`${s.id}.${r.id}`] ?? []));
  // camelCase field names ("distanceScale") read as words too.
  return parts
    .filter(Boolean)
    .join(" \n ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .toLowerCase();
}

const hit = (text: string, word: string) => expand(word).some((w) => text.includes(w));

/**
 * Filter one group's sections. Empty query → unchanged. `forceOpen` expands
 * every surviving section so results are visible without a click.
 */
export function filterSections(
  sections: SectionDef[],
  query: string,
  tabId: TabId,
): { sections: SectionDef[]; forceOpen: boolean; rows: number } {
  const words = tokenize(query);
  if (!words.length) return { sections, forceOpen: false, rows: 0 };
  const out: SectionDef[] = [];
  let rows = 0;
  for (const s of sections) {
    const st = sectionText(s, tabId);
    if (words.every((w) => hit(st, w))) {
      out.push(s);
      rows += s.rows.length;
      continue;
    }
    // A row matches when every word is found in it or its section, and at
    // least one word is the row's own (else "reflections intensity" would
    // return every row of the Reflections section).
    const kept = s.rows.filter((r) => {
      const rt = rowText(s, r);
      return words.some((w) => hit(rt, w)) && words.every((w) => hit(rt, w) || hit(st, w));
    });
    if (kept.length) {
      out.push({ ...s, rows: kept });
      rows += kept.length;
    }
  }
  return { sections: out, forceOpen: true, rows };
}

/** One surviving section plus the group that owns it. */
export interface TabHit {
  tabId: TabId;
  section: SectionDef;
}

/** Search every group at once; `counts` is matching rows per group (for the rail). */
export function filterAllTabs(
  sources: Array<{ tabId: TabId; sections: SectionDef[] }>,
  query: string,
): { hits: TabHit[]; forceOpen: boolean; counts: Partial<Record<TabId, number>>; rows: number } {
  const hits: TabHit[] = [];
  const counts: Partial<Record<TabId, number>> = {};
  let rows = 0;
  if (!tokenize(query).length) return { hits, forceOpen: false, counts, rows };
  for (const { tabId, sections } of sources) {
    const r = filterSections(sections, query, tabId);
    for (const section of r.sections) hits.push({ tabId, section });
    if (r.rows) counts[tabId] = r.rows;
    rows += r.rows;
  }
  return { hits, forceOpen: true, counts, rows };
}
